import { Component, computed, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PermissionsService } from '../../core/services/permissions.service';
import {
    HealFamiliesResult,
    HealRun,
    ProductFamilyAssemblyDetail,
    ProductFamilySummary
} from '../../core/models';

// Per-location configurable-family tab — the ASSEMBLY truth from the publisher side (FAM- parents,
// linked members, per-member exclusion reasons), read-only except for one lever: "Heal site
// families", which discovers the store's simple products straight from Magento and re-runs the
// idempotent assembly. The list is grouped from member sightings, so families that never assembled
// (the two same-name bottles still showing as two tiles) appear here too — with the reason.
@Component({
    selector: 'app-families-tab',
    standalone: true,
    imports: [
        ButtonModule, TableModule, TagModule, DialogModule, MessageModule,
        InputTextModule, ToggleSwitchModule, TooltipModule, FormsModule, DatePipe
    ],
    template: `
        <div class="flex flex-wrap items-center gap-2 mb-3">
            <input pInputText [(ngModel)]="search" (keyup.enter)="reload()" placeholder="Search name / family key" class="w-72" />
            <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
            <p-button icon="pi pi-refresh" [text]="true" size="small" (onClick)="reload()" />
            <span class="flex-1"></span>
            @if (canHeal()) {
                <p-button icon="pi pi-wrench" label="Heal site families" size="small" severity="warn"
                          [loading]="healing()" (onClick)="confirmHeal.set(true)" />
            }
        </div>

        @if (healRun(); as hr) {
            @if (runStale()) {
                <p-message severity="warn" styleClass="mb-3 w-full">
                    The last heal run for this store stopped — no progress since
                    {{ hr.lastProgressAt | date: 'short' }}. Start a new heal.
                </p-message>
            } @else if (hr.status === 'Queued') {
                <p-message severity="warn" styleClass="mb-3 w-full">
                    Heal queued — waiting to start. If this does not move within a minute, check the
                    Consumers screen.
                </p-message>
            } @else if (hr.status === 'Running') {
                <p-message severity="info" styleClass="mb-3 w-full">
                    Healing this store…
                    {{ hr.familiesTotal > 0
                        ? 'assembling families ' + hr.familiesProcessed + '/' + hr.familiesTotal
                        : 'discovering products — ' + hr.productsDiscovered + ' so far' }}
                    (safe to leave this page)
                </p-message>
            }
            @if (hr.log && (hr.status === 'Queued' || hr.status === 'Running' || hr.status === 'Failed')) {
                <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-2 mb-3 text-xs"
                     style="max-height: 12rem; overflow: auto">
                    <pre class="m-0 whitespace-pre-wrap">{{ hr.log }}</pre>
                </div>
            }
        }
        @if (error()) {
            <p-message severity="error" styleClass="mb-3 w-full">{{ error() }}</p-message>
        }

        <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true" [rows]="pageSize"
                 [totalRecords]="total()" (onLazyLoad)="onPage($event)" [first]="first()">
            <ng-template pTemplate="header">
                <tr>
                    <th>Family</th><th>Parent SKU</th><th>Members</th><th>Sized</th><th>Linked</th>
                    <th>Status</th><th>Last assembled</th><th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-f>
                <tr class="cursor-pointer" (click)="open(f)">
                    <td>{{ f.displayName }}
                        <span class="text-surface-400 text-xs block">{{ f.familyKey }}</span>
                    </td>
                    <td class="font-medium whitespace-nowrap">{{ f.parentSku }}
                        @if (f.parentMagentoId) { <span class="text-surface-400 text-xs block">Magento #{{ f.parentMagentoId }}</span> }
                    </td>
                    <td class="tabular-nums text-center">{{ f.memberCount }}</td>
                    <td class="tabular-nums text-center">{{ f.sizedCount }}</td>
                    <td class="tabular-nums text-center">{{ f.linkedCount }}</td>
                    <td><p-tag [value]="statusOf(f)" [severity]="statusSev(f)" /></td>
                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ f.lastAssembledAt ? (f.lastAssembledAt | date: 'short') : '—' }}</td>
                    <td><p-button icon="pi pi-eye" [text]="true" size="small" (onClick)="open(f)" /></td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr><td colspan="8">{{ error() ?? 'No families for this location yet — run "Heal site families" to find them.' }}</td></tr>
            </ng-template>
        </p-table>

        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '52rem' }" [dismissableMask]="true"
                  [header]="'Family ' + (detail()?.displayName ?? '')">
            @if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detailError()) {
                <p-message severity="error">{{ detailError() }}</p-message>
            } @else if (detail(); as d) {
                <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 text-sm">
                    <div><b>Family key:</b> {{ d.familyKey }}</div>
                    <div><b>Parent SKU:</b> {{ d.parentSku }}</div>
                    <div><b>Magento parent:</b> {{ d.parentMagentoId ? '#' + d.parentMagentoId : 'not created' }}</div>
                    <div class="col-span-2 md:col-span-3 text-surface-500">
                        <b>Last assembled:</b> {{ d.lastAssembledAt ? (d.lastAssembledAt | date: 'medium') : 'never' }}
                    </div>
                </div>
                <p-table [value]="d.members" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr><th>SKU</th><th>Name</th><th>Size</th><th>Status<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="NoSize: no bottle size found. DuplicateSize: another member already has that size." tooltipPosition="top"></i></th><th>Magento ID</th><th>Location</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-m>
                        <tr>
                            <td class="font-medium whitespace-nowrap">{{ m.sku }}</td>
                            <td class="text-sm">{{ m.name }}</td>
                            <td class="whitespace-nowrap">{{ m.volumeLabel || '—' }}</td>
                            <td><p-tag [value]="m.status" [severity]="memberSev(m.status)" /></td>
                            <td class="text-sm text-surface-500">{{ m.magentoProductId || '—' }}</td>
                            <td class="text-sm text-surface-500">{{ m.locationId }}</td>
                        </tr>
                    </ng-template>
                </p-table>
            }
        </p-dialog>

        <p-dialog header="Heal site families" [(visible)]="confirmHeal" [modal]="true" [style]="{ width: '32rem' }">
            <p class="m-0 mb-3 text-sm">
                This changes the live Magento catalog: products with the same name are grouped by size under
                a new parent product, and the grouped items are hidden. Continue?
            </p>
            <div class="flex items-start gap-2">
                <p-toggleswitch [(ngModel)]="recalcFirst" inputId="recalcFirst" />
                <label for="recalcFirst" class="text-sm">
                    <b>Recalculate first</b><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Re-reads every size and rebuilds all families. Use after fixing sizes on already grouped products. Slower." tooltipPosition="top"></i>
                </label>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" [text]="true" (onClick)="confirmHeal.set(false)" />
                <p-button label="Heal now" severity="warn" (onClick)="heal()" />
            </ng-template>
        </p-dialog>

        <p-dialog header="Heal report" [(visible)]="showReport" [modal]="true" [style]="{ width: '56rem' }" [dismissableMask]="true">
            @if (report(); as r) {
                <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-sm">
                    <div><b>Products discovered:</b> <span class="tabular-nums">{{ r.productsDiscovered }}</span></div>
                    <div><b>Lone products (1 of a name):</b> <span class="tabular-nums">{{ r.loneProducts }}</span></div>
                    <div><b>Families (2+ members):</b> <span class="tabular-nums">{{ r.familiesSeen }}</span></div>
                    <div><b>Assembled now:</b> <span class="tabular-nums">{{ r.familiesAssembled }}</span></div>
                    <div><b>Already assembled:</b> <span class="tabular-nums">{{ r.familiesAlreadyAssembled }}</span></div>
                    <div><b>Missing sizes:</b> <span class="tabular-nums">{{ r.familiesTooSmall }}</span></div>
                    <div><b>Deferred:</b> <span class="tabular-nums">{{ r.familiesDeferred }}</span></div>
                    <div><b>Failed:</b> <span class="tabular-nums">{{ r.familiesFailed }}</span></div>
                    @if (r.familiesBlocked > 0) {
                        <div class="col-span-2"><b>Assembly blocked:</b> <span class="tabular-nums">{{ r.familiesBlocked }}</span>
                            <span class="text-xs text-surface-500 block">Not written to Magento yet — see run log.</span>
                        </div>
                    }
                </div>
                @if (r.attention.length === 0) {
                    <p-message severity="success">Every discovered family is fully assembled — nothing needs attention.</p-message>
                } @else {
                    <h6 class="mt-0">Needs attention ({{ r.attention.length }}@if (r.attentionTruncated) {, truncated})<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Fix the size of the flagged product in XT or Magento, then heal again." tooltipPosition="top"></i></h6>
                    @for (fam of r.attention; track fam.familyKey) {
                        <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-3 mb-2">
                            <div class="flex flex-wrap items-center gap-2 mb-2">
                                <span class="font-medium">{{ fam.displayName }}</span>
                                <p-tag [value]="fam.outcome" [severity]="outcomeSev(fam.outcome)" />
                                <span class="text-xs text-surface-400">{{ fam.parentSku }} · {{ fam.linkedCount }}/{{ fam.memberCount }} linked</span>
                            </div>
                            @if (fam.error) {
                                <p-message severity="error" styleClass="mb-2 w-full">{{ fam.error }}</p-message>
                            }
                            <p-table [value]="fam.members" styleClass="p-datatable-sm">
                                <ng-template pTemplate="body" let-m>
                                    <tr>
                                        <td class="font-medium whitespace-nowrap">{{ m.sku }}</td>
                                        <td class="text-sm">{{ m.name }}</td>
                                        <td class="whitespace-nowrap">{{ m.volumeLabel || '—' }}</td>
                                        <td><p-tag [value]="m.status" [severity]="memberSev(m.status)" /></td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>
                    }
                }
                @if (healRun()?.log; as log) {
                    <h6>Run log</h6>
                    <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-2 text-xs"
                         style="max-height: 12rem; overflow: auto">
                        <pre class="m-0 whitespace-pre-wrap">{{ log }}</pre>
                    </div>
                }
            }
        </p-dialog>
    `
})
export class FamiliesTab implements OnDestroy {
    private readonly api = inject(MiddlewareApi);
    private readonly perms = inject(PermissionsService);

    // Signal input — the parent (merchant-detail tabs) passes [locationId].
    readonly locationId = input.required<string>();

    private latestCheckedFor: string | null = null;
    private pollHandle: ReturnType<typeof setInterval> | null = null;

    constructor() {
        // On tab load, adopt any run already alive for this store (started by us before a refresh,
        // or by another operator) — the run lives on the server, the tab only watches it.
        effect(() => {
            const loc = this.locationId();
            if (loc && this.latestCheckedFor !== loc) {
                this.latestCheckedFor = loc;
                this.api.getLatestHealRun(loc).subscribe({
                    next: (run) => {
                        if (run.status !== 'Queued' && run.status !== 'Running') return;
                        this.healRun.set(run);
                        if (this.isDead(run)) {
                            // A dead run must not hold the Heal button hostage — surface it,
                            // leave the button usable, and let a new heal supersede it.
                            this.runStale.set(true);
                        } else {
                            this.healing.set(true);
                            this.startPolling();
                        }
                    },
                    error: () => { /* 404 = never healed; nothing to adopt */ }
                });
            }
        });
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    readonly items = signal<ProductFamilySummary[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly pageSize = 25;

    search = '';
    recalcFirst = false;

    readonly canHeal = computed(() => this.perms.has('products:manage'));

    // Detail dialog state.
    readonly showDetail = signal(false);
    readonly detail = signal<ProductFamilyAssemblyDetail | null>(null);
    readonly detailLoading = signal(false);
    readonly detailError = signal<string | null>(null);

    // Heal state — the server-side run this tab is watching.
    readonly confirmHeal = signal(false);
    readonly healing = signal(false);
    readonly healRun = signal<HealRun | null>(null);
    readonly runStale = signal(false);
    readonly showReport = signal(false);
    readonly report = signal<HealFamiliesResult | null>(null);

    /** Matches the server's enqueue guard: a run whose heartbeat stopped >3 min ago is dead. */
    private static readonly StaleMs = 3 * 60 * 1000;
    private isDead(run: HealRun): boolean {
        const heartbeat = run.lastProgressAt ? Date.parse(run.lastProgressAt) : NaN;
        return !Number.isFinite(heartbeat) || Date.now() - heartbeat > FamiliesTab.StaleMs;
    }

    onPage(e: { first?: number | null; rows?: number | null }): void {
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        const loc = this.locationId();
        // Guard: the [lazy] table fires onLazyLoad on first render, which can precede the input being set.
        if (!loc) { this.loading.set(false); return; }
        this.loading.set(true);
        this.error.set(null);
        this.api.getProductFamilies({
            locationId: loc,
            page,
            pageSize: this.pageSize,
            search: this.search || undefined
        }).subscribe({
            next: (r) => {
                this.items.set(r.items ?? []);
                this.total.set(r.total ?? 0);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
            }
        });
    }

    open(f: ProductFamilySummary): void {
        this.showDetail.set(true);
        this.detail.set(null);
        this.detailError.set(null);
        this.detailLoading.set(true);
        this.api.getProductFamilyAssembly(f.familyKey).subscribe({
            next: (d) => { this.detail.set(d); this.detailLoading.set(false); },
            error: (e) => { this.detailError.set(this.describe(e)); this.detailLoading.set(false); }
        });
    }

    heal(): void {
        const loc = this.locationId();
        if (!loc) return;
        this.confirmHeal.set(false);
        this.healing.set(true);
        this.error.set(null);
        this.runStale.set(false);
        // Enqueue only — the pass runs on the server's bus. If a run is already alive for this
        // store the API returns THAT run, so a double-click or a second operator just watches it.
        this.api.healSiteFamilies(loc, this.recalcFirst).subscribe({
            next: (run) => {
                this.healRun.set(run);
                this.startPolling();
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.healing.set(false);
            }
        });
    }

    private startPolling(): void {
        this.stopPolling();
        this.pollHandle = setInterval(() => this.pollRun(), 2000);
    }

    private stopPolling(): void {
        if (this.pollHandle !== null) {
            clearInterval(this.pollHandle);
            this.pollHandle = null;
        }
    }

    private pollRun(): void {
        const current = this.healRun();
        if (!current) { this.stopPolling(); return; }
        this.api.getHealRun(current.runId).subscribe({
            next: (run) => {
                this.healRun.set(run);
                if ((run.status === 'Queued' || run.status === 'Running') && this.isDead(run)) {
                    // The run we were watching died under us (or never had a message to begin
                    // with) — release the button and say so instead of polling forever.
                    this.stopPolling();
                    this.healing.set(false);
                    this.runStale.set(true);
                    return;
                }
                if (run.status === 'Completed') {
                    this.stopPolling();
                    this.healing.set(false);
                    this.report.set(run.report ?? null);
                    this.showReport.set(true);
                    this.reload();
                } else if (run.status === 'Failed') {
                    this.stopPolling();
                    this.healing.set(false);
                    this.error.set(run.error ?? 'The heal failed — check the API logs.');
                }
            },
            // Transient poll errors (redeploy blip, network) — keep polling; the run is server-side.
            error: () => {}
        });
    }

    statusOf(f: ProductFamilySummary): string {
        // The API only returns 2+ member families (a lone product is not a family).
        if (f.linkedCount === f.memberCount) return 'Assembled';
        // Every sized member is linked — the configurable is live; the leftovers are members the
        // assembler correctly refused for lack of a size. Say so, instead of a vague "Partial".
        if (f.linkedCount >= 2 && f.linkedCount === f.sizedCount) {
            const unsized = f.memberCount - f.sizedCount;
            return `Assembled · ${unsized} without size`;
        }
        if (f.sizedCount < 2) return 'Needs sizes';
        if (f.linkedCount > 0) return 'Partial';
        return 'Not assembled';
    }

    statusSev(f: ProductFamilySummary): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const status = this.statusOf(f);
        if (status.startsWith('Assembled ·')) return 'warn';
        switch (status) {
            case 'Assembled': return 'success';
            case 'Partial': return 'warn';
            case 'Needs sizes': return 'danger';
            case 'Not assembled': return 'info';
            default: return 'secondary';
        }
    }

    memberSev(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case 'Linked': return 'success';
            case 'Unlinked': return 'info';
            case 'AwaitingMagentoId': return 'warn';
            case 'NoSize': return 'danger';
            case 'DuplicateSize': return 'danger';
            default: return 'secondary';
        }
    }

    outcomeSev(outcome: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (outcome) {
            case 'Assembled': return 'success';
            case 'AlreadyAssembled': return 'info';
            case 'TooSmall': return 'warn';
            case 'OptionsDeferred': return 'warn';
            case 'AssemblyBlocked': return 'warn';
            case 'Failed': return 'danger';
            default: return 'secondary';
        }
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the required products permission.';
        const server = e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
